import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthContext } from './AuthContext';
import { useToast } from './ToastContext';
import { tiers } from '../data/tiersData';
import { loadTradingProfile, saveTradingProfile, recommendInstrument } from '../utils/sizing';

const AppContext = createContext(null);

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
};

export const AppProvider = ({ children }) => {
  const auth = useAuthContext();
  const { toast } = useToast();
  const [time, setTime] = useState(new Date());

  // Stream state
  const [isStreaming, setIsStreaming] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Account tier
  const [accountSize, setAccountSize] = useState('');
  const [activeTier, setActiveTier] = useState(null);

  // Personalized trading profile (V1: localStorage-backed)
  const [tradingProfile, setTradingProfileState] = useState(() => loadTradingProfile());
  const [showProfileModal, setShowProfileModal] = useState(false);

  const setTradingProfile = useCallback((next) => {
    if (!next) {
      saveTradingProfile(null);
      setTradingProfileState(null);
      return;
    }
    const profile = {
      accountSize: Number(next.accountSize) || null,
      riskPercent: Number(next.riskPercent) || 1,
      instrument: next.instrument || recommendInstrument(Number(next.accountSize)),
    };
    saveTradingProfile(profile);
    setTradingProfileState(profile);
  }, []);

  // Modal visibility
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);
  const [showTrialOfferModal, setShowTrialOfferModal] = useState(false);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showDiscountOfferModal, setShowDiscountOfferModal] = useState(false);
  const [showDiscountConfirmation, setShowDiscountConfirmation] = useState(false);
  const [showCancellationConfirmation, setShowCancellationConfirmation] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);

  // Business logic flags
  const [hasDiscount, setHasDiscount] = useState(false);
  const [hasEverHadDiscount, setHasEverHadDiscount] = useState(false);
  const [hasEverBeenSubscribed, setHasEverBeenSubscribed] = useState(false);
  const [hasCompletedTrial, setHasCompletedTrial] = useState(false);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Tier calculation
  const calculateTier = useCallback((size) => {
    if (!size) return null;
    const numericSize = parseInt(size.toString().replace(/,/g, ''));
    const tier = tiers.find(
      (t) => numericSize >= t.minAccountSize && numericSize <= t.maxAccountSize
    );
    return tier ? tier.tier : null;
  }, []);

  useEffect(() => {
    setActiveTier(accountSize ? calculateTier(accountSize) : null);
  }, [accountSize, calculateTier]);

  // Sync subscription state
  useEffect(() => {
    const wasSubscribed = auth.isSubscribed;
    if (!wasSubscribed && hasEverBeenSubscribed) {
      setHasCompletedTrial(true);
    }
    if (!auth.isSubscribed && !hasEverBeenSubscribed) {
      setHasCompletedTrial(false);
    }
  }, [auth.isSubscribed, hasEverBeenSubscribed]);

  const handleJoinStream = useCallback(() => {
    if (!auth.isSubscribed) {
      setShowTrialOfferModal(true);
      return;
    }
    setConnecting(true);
    setTimeout(() => {
      setIsStreaming(true);
      setConnecting(false);
    }, 1500);
  }, [auth.isSubscribed]);

  const exitStream = useCallback(() => {
    setIsStreaming(false);
    setIsFullscreen(false);
  }, []);

  const handleLogoutCleanup = useCallback(async () => {
    await auth.signOut();
    setAccountSize('');
    setActiveTier(null);
    setHasCompletedTrial(false);
    setShowLogoutConfirmModal(false);
  }, [auth]);

  const handleStartTrial = useCallback(() => {
    setShowTrialOfferModal(false);
    auth.handleSubscribe();
    setHasEverBeenSubscribed(true);
    setHasDiscount(false);
    setShowSubscriptionSuccess(true);
    toast.success('Subscription activated!');
  }, [auth, toast]);

  const handleCancelSubscription = useCallback(() => {
    setShowCancelConfirmModal(false);
    if (hasEverHadDiscount) {
      auth.handleUnsubscribe();
      setShowBillingModal(false);
      setShowAccountModal(false);
      setShowCancellationConfirmation(true);
    } else {
      setShowDiscountOfferModal(true);
    }
  }, [auth, hasEverHadDiscount]);

  const handleAcceptDiscount = useCallback(() => {
    setHasDiscount(true);
    setHasEverHadDiscount(true);
    setShowDiscountOfferModal(false);
    setShowDiscountConfirmation(true);
    toast.success('50% discount applied!');
  }, [toast]);

  const handleDeclineDiscount = useCallback(() => {
    setShowDiscountOfferModal(false);
    auth.handleUnsubscribe();
    setShowBillingModal(false);
    setShowAccountModal(false);
    setShowCancellationConfirmation(true);
    toast.info('Subscription cancelled');
  }, [auth, toast]);

  return (
    <AppContext.Provider value={{
      time,
      isStreaming, setIsStreaming,
      connecting, setConnecting,
      volume, setVolume,
      isMuted, setIsMuted,
      isFullscreen, setIsFullscreen,
      accountSize, setAccountSize,
      activeTier, setActiveTier,
      calculateTier,
      tradingProfile, setTradingProfile,
      showProfileModal, setShowProfileModal,
      showAccountModal, setShowAccountModal,
      showCancelConfirmModal, setShowCancelConfirmModal,
      showLogoutConfirmModal, setShowLogoutConfirmModal,
      showTrialOfferModal, setShowTrialOfferModal,
      showSubscriptionSuccess, setShowSubscriptionSuccess,
      showBillingModal, setShowBillingModal,
      showDiscountOfferModal, setShowDiscountOfferModal,
      showDiscountConfirmation, setShowDiscountConfirmation,
      showCancellationConfirmation, setShowCancellationConfirmation,
      showUpdatePaymentModal, setShowUpdatePaymentModal,
      hasDiscount, setHasDiscount,
      hasEverHadDiscount,
      hasEverBeenSubscribed,
      hasCompletedTrial,
      handleJoinStream,
      exitStream,
      handleLogoutCleanup,
      handleStartTrial,
      handleCancelSubscription,
      handleAcceptDiscount,
      handleDeclineDiscount,
    }}>
      {children}
    </AppContext.Provider>
  );
};
